<template>
  <v-card id="base-rate-finder">
    <v-card-title :class="headerClass">
      <v-icon color="tertiary" class="mr-2">{{ baseRateMop.path }}</v-icon>
      <span class="headline">{{ $t('baseRateFinder') }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh :hideCancel="contractApprovalId !== undefined"/>
    </v-card-title>
    <v-card-text class="pt-4">
      <v-row dense>
        <v-col cols="12" md="auto" style="min-width: 384px">
          <ContractKeyCard :contract="summary"/>
        </v-col>
        <v-col cols="12" md="auto" style="min-width: 864px">
          <DetailCard :title="$t('totals')">
            <v-row no-gutters>
              <v-col v-for="(column, index) in totalColumns" :key="index" cols="6">
                <v-row no-gutters v-for="total in column" :key="total.label">
                  <v-col>
                    {{ total.label }}:
                  </v-col>
                  <v-col>
                    {{ total.value }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </DetailCard>
        </v-col>
      </v-row>

      <v-alert v-if="definitionFailures.length > 0" type="warning" text class="body-2 mt-4 mb-0">
        <div class="mb-1">{{ $t('definitionLinesNotAdded') }}</div>
        <div v-for="(failure, index) in definitionFailures" :key="index">
          {{ failure.rateSheetDefinitionName }} · {{ rateSheetLineLabel(failure.line) }} · {{ failure.message }}
        </div>
      </v-alert>

      <v-alert v-if="hasPendingScheduledRateChange" type="warning" text class="body-2 mt-4 mb-0">
        {{ $t('scheduledRateChangeExistsWarning') }}
      </v-alert>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4"/>
      <NoData v-else-if="activities.length === 0" class="mt-4">{{ $t('noBaseRateTicketsOnContract') }}</NoData>

      <DetailCard
      v-for="activity in activities"
      :key="activityKey(activity)"
      :title="activity.activityName"
      :fillHeight="false"
      elementClass="mt-4">
        <template #title.adjacent>
          <span class="caption grey--text ml-2">{{ activity.rateSheetDefinitionName }}</span>
        </template>
        <template #title.append>
          <span class="caption grey--text mr-2">
            {{ $t('baseRateAmount', { rate: formatMoney(activity.baseRate) }) }} · {{ $tc('baseRateHitCount', hitCountFor(activity)) }}
          </span>
          <Icon
          v-if="canAddLinesTo(activity)"
          icon="mdi-plus"
          elementId="base-rate-finder-add-line"
          :small="false"
          :tooltipText="$t('newRate')"
          @icon-clicked="openLineEditor(activity)"/>
        </template>

        <v-alert v-if="activity.namedRateName" dense text type="info" class="body-2 mb-3">
          <template #prepend>
            <v-icon small color="info" class="mr-2">mdi-information</v-icon>
          </template>
          {{ $t('baseRateActivityNamedRate', { namedRate: activity.namedRateName }) }}
        </v-alert>
        <v-alert v-else-if="activity.expirationDate" dense text type="info" class="body-2 mb-3">
          {{ $t('baseRateActivityExpired', { date: utcToLocalDate(activity.expirationDate) }) }}
        </v-alert>

        <v-data-table
        id="base-rate-finder-combination-table"
        dense
        hide-default-footer
        show-expand
        single-expand
        class="combination-table"
        item-key="key"
        :headers="combinationHeaders"
        :items="combinationItemsByActivityKey[activityKey(activity)]"
        :items-per-page="-1"
        :expanded="expandedFor(activity)"
        @update:expanded="setExpanded(activity, $event)"
        @click:row="(item, row) => row.expand(!row.isExpanded)">
          <template #item.combination="{ item }">
            <span class="font-weight-medium">{{ combinationLabel(item) }}</span>
            <Icon
            v-if="item.coveredByPendingLine"
            icon="mdi-check-circle"
            iconColor="success"
            margin="ml-2"
            :tooltipText="$t('coveredByPendingLine')"/>
          </template>
          <template #item.grossVolume="{ item }">{{ mbfStringFromBf(item.grossVolume) }}</template>
          <template #item.netVolume="{ item }">{{ mbfStringFromBf(item.netVolume) }}</template>
          <template #item.tickets="{ item }">{{ $tc('contractTicketCount', item.tickets.length) }}</template>
          <template #expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-3 grey lighten-5">
              <BaseRateCombinationDiagnosis
              :key="item.key"
              :combination="item"
              :sheetLines="existingSheetLinesFor(activity)"
              :accountId="activity.scalingAccountId"
              :lookup="lookupFor(activity)"
              :scalingGroups="scalingGroupsByAccountId[activity.scalingAccountId]"
              @ticket-selected="ticketSelected"/>
            </td>
          </template>
        </v-data-table>

        <template v-if="pendingLinesFor(activity).length > 0">
          <div class="overline text--secondary mt-3 mb-3">{{ $t('pendingRateSheetLines') }}</div>
          <RateSheetLineTable
          id="base-rate-finder-pending-lines-table"
          :rateSheetInteractionMode="RateSheetInteractionMode.RateSheetDefinition.Create"
          :lines="pendingLinesFor(activity)"
          :originalLines="pendingLinesFor(activity)"
          :accountId="activity.scalingAccountId"
          :scalingGroups="scalingGroupsByAccountId[activity.scalingAccountId]"
          @edit="({ line }) => editPendingLine(activity, line)"
          @delete="({ line }) => removePendingLine(activity, line)"/>
          <div v-if="definitionLineCountFor(activity) > 0" class="caption text--secondary mt-1">
            {{ $t('pendingLinesAlsoAddedToDefinition', { count: definitionLineCountFor(activity), rateSheetDefinition: activity.rateSheetDefinitionName }) }}
          </div>
        </template>
      </DetailCard>
    </v-card-text>
    <v-card-actions v-if="canManageContracts" class="px-4 pb-4">
      <template v-if="definitionFailures.length > 0">
        <v-spacer/>
        <v-btn id="base-rate-finder-close" color="primary" @click="$emit('saved', summary.contractId)">{{ $t('close') }}</v-btn>
      </template>
      <template v-else>
        <span class="caption text--secondary">{{ $t('baseRateSaveHint') }}</span>
        <v-spacer/>
        <v-btn id="base-rate-finder-save" color="primary" :disabled="pendingLineCount === 0 && contractApprovalId === undefined" :loading="saving" @click="trySave">
          {{ contractApprovalId !== undefined && pendingLineCount === 0 ? $t('finishEditingContract') : $tc('savePendingLines', pendingLineCount) }}
        </v-btn>
      </template>
    </v-card-actions>

    <Dialog :stateId="lineEditorDialogId" @close="closeLineEditor">
      <BaseRateLineEditor
      v-if="lineEditor"
      :headerClass="headerClass"
      :account="{ accountId: summary.destinationAccountId, name: summary.destination }"
      :rateSheetDefinition="{ rateSheetDefinitionId: lineEditor.activity.rateSheetDefinitionId, name: lineEditor.activity.rateSheetDefinitionName }"
      :sheetLines="lineEditor.sheetLines"
      :combinations="lineEditor.activity.combinations"
      :line="lineEditor.line"
      :definitionLines="activeDefinitionVersionFor(lineEditor.activity)?.rateSheetDefinitionLines"
      :lookup="lookupFor(lineEditor.activity)"
      @save="stageLine"
      @save-and-close="stageLineAndClose"
      @cancel="closeLineEditor"/>
    </Dialog>

    <Dialog :stateId="ticketDetailDialogId" @close="focusedTicketId = undefined">
      <TicketDetails
      v-if="focusedTicketId !== undefined"
      :ticketId="focusedTicketId"
      :showCrud="false"
      initialTab="scaling"
      @close="closeTicketDetail"/>
    </Dialog>

    <Dialog :stateId="uncoveredCombinationsDialogId" @close="uncoveredCombinations = null">
      <UncoveredLogCombinationsDialog
      v-if="uncoveredCombinations"
      :gap="uncoveredCombinations"/>
    </Dialog>

    <v-dialog v-model="confirmSubmitDialog" width="400px">
      <ConfirmDialog
      id="base-rate-finder-submit-dialog"
      :title="$t('confirmSubmitContractTitle')"
      :body="$t('confirmSubmitContractMessage')"
      color="primary"
      :confirmText="$t('submit')"
      @confirm="save"
      @cancel="confirmSubmitDialog = false">
        <template #custom-body>
          <v-textarea
          v-model="contractApprovalNote"
          :label="$t('note')"
          outlined
          auto-grow
          counter
          maxlength="1024"
          clearable
          dense
          color="black"/>
        </template>
      </ConfirmDialog>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatMoney, mbfStringFromBf, numberWithCommas, uuidv4 } from '@/utils/NumericMutations'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { expansionCoverageGapFromError } from '@/utils/ExpansionCoverage.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { ContractApprovalStatus, UserClaims } from '@/utils/Enumerations.js'
import { baseRateMop } from '@/images/AngelSvgLibrary'
import { RateSheetInteractionMode } from '@/components/activity/rate-sheet/RateSheet'
import { buildScalingLookup, lineDimensionIds, lineMatcher, sameDimensions } from '@/utils/RateSheetMatching'
import { getActiveRateSheetVersion } from '@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitions'
import { combinationKey, combinationLabel, rateSheetLineLabel } from '@/components/contract/base-rate-finder/BaseRateFinder'

export default {
  name: 'BaseRateFinder',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    BaseRateCombinationDiagnosis: () => import('@/components/contract/base-rate-finder/BaseRateCombinationDiagnosis.vue'),
    BaseRateLineEditor: () => import('@/components/contract/base-rate-finder/BaseRateLineEditor.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    ContractKeyCard: () => import('@/components/contract/ContractKeyCard.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    RateSheetLineTable: () => import('@/components/activity/rate-sheet/RateSheetLineTable.vue'),
    TicketDetails: () => import('@/components/ticket/ticket-details/TicketDetails.vue'),
    UncoveredLogCombinationsDialog: () => import('@/components/activity/rate-sheet/UncoveredLogCombinationsDialog.vue')
  },

  props: {
    summary: {
      type: Object,
      required: true
    },
    contractApprovalId: {
      type: Number,
      required: false,
      default: undefined
    },
    approvalLocked: {
      type: Boolean,
      required: false,
      default: false
    },
    headerClass: {
      type: String,
      required: true
    }
  },

  data: () => ({
    baseRateMop,
    RateSheetInteractionMode,
    rateSheetsById: {},
    pendingLinesByActivityId: {},
    activeDefinitionVersionsByDefinitionId: {},
    scalingGroupsByAccountId: {},
    expandedByActivityKey: {},
    definitionFailures: [],
    detail: undefined,
    loading: false,
    confirmSubmitDialog: false,
    contractApprovalNote: '',
    lineEditor: undefined,
    focusedTicketId: undefined,
    uncoveredCombinations: null,
    lineEditorDialogId: uniqueDialogId('base-rate-line-editor'),
    ticketDetailDialogId: uniqueDialogId('base-rate-ticket-detail'),
    uncoveredCombinationsDialogId: uniqueDialogId('base-rate-uncovered-log-combinations')
  }),

  computed: {
    ...mapGetters('base-rate-finder', ['saving']),

    activities () {
      return this.detail?.activities ?? []
    },

    hasPendingScheduledRateChange () {
      return this.summary.hasPendingScheduledRateChange
    },

    lookupsByAccountId () {
      return Object.fromEntries(Object.entries(this.scalingGroupsByAccountId)
        .map(([accountId, scalingGroups]) => [accountId, buildScalingLookup(scalingGroups)]))
    },

    combinationItemsByActivityKey () {
      return Object.fromEntries(this.activities.map(activity => {
        const activityKey = this.activityKey(activity)
        const matchSheetLine = this.pendingLinesFor(activity).length > 0
          ? lineMatcher(this.sheetLinesFor(activity), this.lookupFor(activity))
          : undefined
        return [activityKey, activity.combinations.map(combination => ({
          ...combination,
          key: `${activityKey}-${combinationKey(combination)}`,
          coveredByPendingLine: !!matchSheetLine && matchSheetLine(combination) !== null
        }))]
      }))
    },

    canManageContracts () {
      return userAssignedClaim(UserClaims.ContractManager)
    },

    pendingLineCount () {
      return Object.values(this.pendingLinesByActivityId).reduce((count, lines) => count + lines.length, 0)
    },

    totalFigures () {
      return [
        { label: this.$t('baseRateTickets'), value: numberWithCommas(this.detail?.ticketCount ?? this.summary.ticketCount) },
        { label: this.$t('activities'), value: numberWithCommas(this.activities.length) },
        { label: this.$t('baseRateLogs'), value: numberWithCommas(this.detail?.logCount ?? 0) },
        { label: this.$t('baseRateHits'), value: numberWithCommas(this.activities.reduce((count, activity) => count + this.hitCountFor(activity), 0)) },
        { label: this.$t('grossMbf'), value: mbfStringFromBf(this.detail?.grossVolume ?? 0) },
        { label: this.$t('netMbf'), value: mbfStringFromBf(this.detail?.netVolume ?? 0) }
      ]
    },

    totalColumns () {
      return [this.totalFigures.slice(0, 3), this.totalFigures.slice(3)]
    },

    combinationHeaders () {
      return [
        { text: this.$t('unmatchedCombination'), value: 'combination', sortable: false },
        { text: this.$t('baseRateHits'), value: 'logCount', align: 'right' },
        { text: this.$t('grossMbf'), value: 'grossVolume', align: 'right' },
        { text: this.$t('netMbf'), value: 'netVolume', align: 'right' },
        { text: this.$t('tickets'), value: 'tickets', sortable: false },
        { text: '', value: 'data-table-expand' }
      ]
    }
  },

  watch: {
    async summary () {
      await this.load()
    },

    pendingLineCount (count) {
      this.$emit('unsaved-changes', count > 0)
    }
  },

  async created () {
    await this.load()
  },

  methods: {
    ...mapActions('base-rate-finder', ['fetchBaseRateSummary', 'addContractRateSheetLines']),
    ...mapActions('account-scaling', ['fetchAccountSpeciesGroups', 'fetchAccountLengthGroups', 'fetchAccountDiameterGroups']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('rate-sheet-definition', ['fetchVersionedRateSheetDefinition', 'createAccountRateSheetDefinitionLine']),
    ...mapActions('contract-approvals', ['updateContractApproval']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    mbfStringFromBf,
    utcToLocalDate,
    combinationLabel,
    rateSheetLineLabel,

    async load () {
      this.loading = true
      try {
        const detail = await this.fetchBaseRateSummary(this.summary.contractId)
        const rateSheetIds = [...new Set(detail.activities.map(activity => activity.rateSheetId))]
        const scalingAccountIds = [...new Set(detail.activities.map(activity => activity.scalingAccountId))]
        const [rateSheets, scalingGroups] = await Promise.all([
          Promise.all(rateSheetIds.map(rateSheetId => this.fetchRateSheet(rateSheetId))),
          Promise.all(scalingAccountIds.map(accountId => this.fetchScalingGroups(accountId)))
        ])
        this.rateSheetsById = Object.fromEntries(rateSheets.map(rateSheet => [rateSheet.rateSheetId, rateSheet]))
        this.scalingGroupsByAccountId = Object.fromEntries(scalingAccountIds.map((accountId, index) => [accountId, scalingGroups[index]]))
        this.detail = detail
      } finally {
        this.loading = false
      }
    },

    async fetchScalingGroups (accountId) {
      const [speciesGroups, lengthGroups, diameterGroups] = await Promise.all([
        this.fetchAccountSpeciesGroups(accountId),
        this.fetchAccountLengthGroups(accountId),
        this.fetchAccountDiameterGroups(accountId)
      ])
      return { speciesGroups, lengthGroups, diameterGroups }
    },

    lookupFor (activity) {
      return this.lookupsByAccountId[activity.scalingAccountId] ?? buildScalingLookup({})
    },

    activityKey (activity) {
      return `${activity.activityId}-${activity.rateSheetId}`
    },

    hitCountFor (activity) {
      return activity.combinations.reduce((count, combination) => count + combination.logCount, 0)
    },

    canAddLinesTo (activity) {
      return this.canManageContracts && !this.hasPendingScheduledRateChange && !activity.expirationDate && !activity.namedRateName && !!this.rateSheetsById[activity.rateSheetId]
    },

    expandedFor (activity) {
      return this.expandedByActivityKey[this.activityKey(activity)] ?? []
    },

    setExpanded (activity, expanded) {
      this.expandedByActivityKey = { ...this.expandedByActivityKey, [this.activityKey(activity)]: expanded }
    },

    pendingLinesFor (activity) {
      return this.pendingLinesByActivityId[activity.activityId] ?? []
    },

    existingSheetLinesFor (activity) {
      return this.rateSheetsById[activity.rateSheetId]?.rateSheetLines ?? []
    },

    sheetLinesFor (activity) {
      return [...this.existingSheetLinesFor(activity), ...this.pendingLinesFor(activity)]
    },

    definitionLineCountFor (activity) {
      return this.pendingLinesFor(activity).filter(line => line.addToDefinition).length
    },

    activeDefinitionVersionFor (activity) {
      return this.activeDefinitionVersionsByDefinitionId[activity.rateSheetDefinitionId]
    },

    async openLineEditor (activity, line) {
      if (this.approvalLocked) {
        this.setSnackError(this.$t('cannotEditContractMessage', { status: ContractApprovalStatus.fromInt(this.summary.approvalStatus) }))
        return
      }

      if (!(activity.rateSheetDefinitionId in this.activeDefinitionVersionsByDefinitionId)) {
        const versions = await this.fetchVersionedRateSheetDefinition({
          accountId: this.summary.destinationAccountId,
          rateSheetDefinitionId: activity.rateSheetDefinitionId
        })
        this.activeDefinitionVersionsByDefinitionId = {
          ...this.activeDefinitionVersionsByDefinitionId,
          [activity.rateSheetDefinitionId]: getActiveRateSheetVersion(versions)
        }
      }
      this.lineEditor = { activity, line, sheetLines: this.editorSheetLinesFor(activity, line) }
      this.openOrUpdateDialog({ id: this.lineEditorDialogId, width: '500px' })
    },

    editPendingLine (activity, tableLine) {
      this.openLineEditor(activity, this.pendingLinesFor(activity).find(pending => pending.uuid === tableLine.uuid))
    },

    editorSheetLinesFor (activity, editingLine) {
      return this.sheetLinesFor(activity).filter(line => !editingLine || line.uuid !== editingLine.uuid)
    },

    closeLineEditor () {
      this.closeDialogsAtOrAbove(this.lineEditorDialogId)
      this.lineEditor = undefined
    },

    stageLine (line) {
      const { activity } = this.lineEditor
      const staged = { ...line, uuid: line.uuid ?? uuidv4(), isPending: true }
      const pendingLines = this.pendingLinesFor(activity)
      const isEdit = pendingLines.some(pending => pending.uuid === staged.uuid)
      this.pendingLinesByActivityId = {
        ...this.pendingLinesByActivityId,
        [activity.activityId]: isEdit
          ? pendingLines.map(pending => pending.uuid === staged.uuid ? staged : pending)
          : [...pendingLines, staged]
      }
      this.lineEditor = { ...this.lineEditor, sheetLines: this.editorSheetLinesFor(activity, this.lineEditor.line) }
    },

    stageLineAndClose (line) {
      this.stageLine(line)
      this.closeLineEditor()
    },

    removePendingLine (activity, line) {
      this.pendingLinesByActivityId = {
        ...this.pendingLinesByActivityId,
        [activity.activityId]: this.pendingLinesFor(activity).filter(pending => pending.uuid !== line.uuid)
      }
    },

    rateSheetLineRequest (line) {
      const ids = lineDimensionIds(line)
      return {
        speciesGroupId: ids.species,
        lengthGroupId: ids.length,
        diameterGroupId: ids.diameter,
        gradeId: ids.grade,
        endUseSortId: ids.endUseSort,
        rate: line.rate
      }
    },

    trySave () {
      if (this.contractApprovalId === undefined) {
        this.save()
      } else {
        this.confirmSubmitDialog = true
      }
    },

    async save () {
      this.confirmSubmitDialog = false
      const activitiesWithLines = this.activities.filter(activity => this.pendingLinesFor(activity).length > 0)

      if (activitiesWithLines.length === 0) {
        if (await this.submitApproval()) this.$emit('finished')
        return
      }

      try {
        await this.addContractRateSheetLines({
          contractId: this.summary.contractId,
          activities: activitiesWithLines.map(activity => ({
            activityId: activity.activityId,
            rateSheetLines: this.pendingLinesFor(activity).map(this.rateSheetLineRequest)
          }))
        })
      } catch (error) {
        this.showUncoveredCombinations(error)
        return
      }

      const approvalSubmitted = this.contractApprovalId === undefined || await this.submitApproval()
      this.definitionFailures = await this.addLinesToDefinitions(activitiesWithLines)
      this.pendingLinesByActivityId = {}

      if (this.definitionFailures.length > 0 || !approvalSubmitted) return

      this.setSnack(this.$t('baseRateLinesSaved'))
      this.$emit('saved', this.summary.contractId)
    },

    showUncoveredCombinations (error) {
      const gap = expansionCoverageGapFromError(error)
      if (!gap) return

      this.uncoveredCombinations = gap
      this.openOrUpdateDialog({ id: this.uncoveredCombinationsDialogId, width: '900px' })
    },

    async submitApproval () {
      try {
        await this.updateContractApproval({
          contractApprovalId: this.contractApprovalId,
          contractId: this.summary.contractId,
          status: ContractApprovalStatus.PendingReview.value,
          note: this.contractApprovalNote?.trim().length > 0 ? { content: this.contractApprovalNote } : null
        })
        return true
      } catch {
        return false
      }
    },

    async addLinesToDefinitions (activitiesWithLines) {
      const definitionLines = []
      for (const activity of activitiesWithLines) {
        for (const line of this.pendingLinesFor(activity).filter(pending => pending.addToDefinition)) {
          const alreadyQueued = definitionLines.some(queued =>
            queued.rateSheetDefinitionId === activity.rateSheetDefinitionId && sameDimensions(queued.line, line))
          if (!alreadyQueued) {
            definitionLines.push({
              rateSheetDefinitionId: activity.rateSheetDefinitionId,
              rateSheetDefinitionName: activity.rateSheetDefinitionName,
              versionedRateSheetId: this.activeDefinitionVersionFor(activity)?.versionedRateSheetId,
              line
            })
          }
        }
      }

      const failures = []
      for (const definitionLine of definitionLines) {
        try {
          await this.createAccountRateSheetDefinitionLine({
            accountId: this.summary.destinationAccountId,
            rateSheetDefinitionId: definitionLine.rateSheetDefinitionId,
            versionedRateSheetId: definitionLine.versionedRateSheetId,
            ...this.rateSheetLineRequest(definitionLine.line)
          })
        } catch (e) {
          failures.push({ ...definitionLine, message: e?.response?.data?.message ?? e?.message ?? this.$t('unknownError') })
        }
      }
      return failures
    },

    ticketSelected (ticket) {
      this.focusedTicketId = ticket.ticketId
      this.openOrUpdateDialog({ id: this.ticketDetailDialogId, width: '80vw' })
    },

    closeTicketDetail () {
      this.closeDialogsAtOrAbove(this.ticketDetailDialogId)
      this.focusedTicketId = undefined
    }
  }
}
</script>

<style scoped>
.combination-table ::v-deep tbody > tr:not(.v-data-table__expanded__content) {
  cursor: pointer;
}
</style>
